import React, { useState, useEffect } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { cn } from "../../utils/cn"
import { Button } from "../ui/button"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import Typography from "@mui/material/Typography"
import Box from "@mui/material/Box"
import { 
  LayoutDashboard, 
  Users, 
  Building2, 
  Shield, 
  Contact, 
  MapPin, 
  ChevronLeft, 
  ChevronDown,
  ChevronRight,
  FileText, 
  LogOut, 
  ListTodo, 
  Tags, 
  FileText as FileTextIcon,
  GraduationCap,
  PhoneCall,
  Briefcase,
  Settings,
  Calculator,
  Mail,
  AlertCircle,
  Ban,
  AppWindow,
  Lock,
  UserCheck,
  ClipboardList,
  Calendar,
  PlayCircle,
  Activity,
  CheckCircle2,
  CheckSquare,
  BarChart3,
  LineChart,
  CreditCard,
  ArrowLeftRight,
  RefreshCw,
  CloudDownload,
  Archive,
  ScrollText,
  Boxes,
  FolderKanban,
  Filter,
  Headphones,
  MessageCircle,
  Fingerprint,
  Plane,
  CalendarCheck2,
  Clock3,
  IdCard,
  Landmark,
  Upload,
  HelpCircle,
  Sparkles,
  Scale,
  WalletCards,
  ShieldCheck,
  FileSearch,
  AlertTriangle
} from "lucide-react"
import { useAuth } from "../../context/AuthContext"
import { useTranslation } from "react-i18next"

export function Sidebar({ collapsed, setCollapsed }) {
  const location = useLocation()
  const navigate = useNavigate()
  const { logout, user } = useAuth()
  const { t } = useTranslation()
  const roleName = (user?.role_name || user?.role || "").toLowerCase()
  const departmentName = (user?.department_name || "").toLowerCase()
  const isAdmin = roleName === "admin"
  const isAgency = roleName === "agenzia"
  const isHrDepartment =
    departmentName.includes("hr") ||
    departmentName.includes("risorse umane") ||
    departmentName.includes("personale")
  const isLegalDepartment =
    departmentName.includes("legale") ||
    departmentName.includes("legal")
  const isEmployeeLikeRole =
    roleName === "user" ||
    roleName.includes("dipendente") ||
    roleName.includes("collaboratore")
  const canAccessHrCore = isAdmin || isHrDepartment || isEmployeeLikeRole
  const canManageHrAnagrafiche = isAdmin || isHrDepartment
  const isAmministrazione =
    departmentName.includes("amministrazione") || departmentName.includes("administration")
  const canAccessPraticheFinanziarie = isAdmin || isAgency || isAmministrazione
  const canAccessScadenzario = isAdmin || roleName === "call_pagamenti" || isAmministrazione

  const [aderUploadInfoOpen, setAderUploadInfoOpen] = useState(false)
  const [aderCodaValidazioneInfoOpen, setAderCodaValidazioneInfoOpen] = useState(false)
  
  // State to track expanded groups
  const [expandedGroups, setExpandedGroups] = useState({
    gestione: false,
    gestione_pratiche: false,
    gestione_anagrafiche: false,
    configurazioni: false,
    config_pratiche: false,
    config_attributi: false,
    config_sistema: false,
    app_lorenzo: false,
    recessi: false,
    urgenze_opposizioni: false,
    passpartout_gestionale: false,
    open_banking: false,
    pratiche_finanziarie: false,
    app_core: false,
    app_registry: false,
    app_permissions: false,
    crm: false,
    crm_cloudtalk: false,
    crm_funnelix: false,
    hr_candidati: false,
    risorse_umane: false,
    asset_management: false,
    scadenzario: false,
    urgenze_opposizioni_parametri: false
  });

  const toggleGroup = (groupKey, siblings = []) => {
    const updateState = (prev) => {
      const next = { ...prev };
      // Se stiamo aprendo il gruppo, chiudiamo tutti i fratelli
      if (!prev[groupKey]) {
        siblings.forEach(sibKey => {
          if (sibKey !== groupKey) {
            next[sibKey] = false;
          }
        });
      }
      // Toggle del gruppo corrente
      next[groupKey] = !prev[groupKey];
      return next;
    };

    if (collapsed) {
      setCollapsed(false);
      // Wait for expansion animation/state update then toggle
      setTimeout(() => {
        setExpandedGroups(prev => updateState(prev));
      }, 50);
    } else {
      setExpandedGroups(prev => updateState(prev));
    }
  };

  const allNavGroups = [
    {
      key: "dashboard",
      type: "single",
      item: { icon: LayoutDashboard, label: t('sidebar.dashboard'), href: "/" }
    },
    {
      key: "crm",
      type: "group",
      label: "CRM",
      icon: Users,
      items: [
        {
          key: "crm_cloudtalk",
          label: "CloudTalk",
          icon: PhoneCall,
          items: [
            { icon: PhoneCall, label: "Chiamate", href: "/cloudtalk" },
            { icon: Calculator, label: "KPI (Andamento)", href: "/cloudtalk/kpi" }
          ]
        },
        {
          key: "crm_funnelix",
          label: "Funnelix",
          icon: Filter,
          items: [
            { icon: Filter, label: "Dashboard", href: "/crm/funnelix" },
            { icon: Calculator, label: "KPI", href: "/crm/funnelix/kpi" }
          ]
        },
        ...(isAdmin ? [{
          key: "crm_spoki",
          label: "Spoki WhatsApp",
          icon: MessageCircle,
          items: [
            { icon: Settings, label: "Impostazioni", href: "/spoki/settings" },
            { icon: FileTextIcon, label: "Template", href: "/spoki/templates" },
            { icon: PlayCircle, label: "Automazioni", href: "/spoki/automations" },
            { icon: ListTodo, label: "Log invii", href: "/spoki/logs" },
            { icon: Activity, label: "Webhook", href: "/spoki/webhooks" },
            { icon: MessageCircle, label: "Chat", href: "/spoki/chat" }
          ]
        }] : []),
        {
          key: "crm_lead_giornalieri",
          label: "Lead Giornalieri",
          icon: CalendarCheck2,
          items: [
            { icon: Calculator, label: "Regole Calcolo", href: "/crm/lead-giornalieri/regole-calcolo" },
            { icon: BarChart3, label: "Kpi Lead", href: "/crm/lead-giornalieri/kpi-lead" }
          ]
        }
      ]
    },
    ...(isAdmin ? [{
      key: "configurazioni",
      type: "group",
      label: t('sidebar.configurations'),
      icon: Settings,
      items: [
        {
          key: "config_pratiche",
          label: "Pratiche",
          icon: ListTodo,
          items: [
            { icon: ListTodo, label: t('sidebar.practiceStates'), href: "/stati-pratica" },
            { icon: Tags, label: t('sidebar.practiceTypes'), href: "/tipi-pratica" },
          ]
        },
        {
          key: "config_attributi",
          label: "Attributi",
          icon: Tags,
          items: [
            { icon: FileTextIcon, label: t('sidebar.relationshipTypes'), href: "/tipi-rapporto" },
            { icon: PhoneCall, label: t('sidebar.eventTypes'), href: "/tipi-evento" },
          ]
        },
        {
          key: "config_sistema",
          label: "Sistema",
          icon: Settings,
          items: [
            { icon: MapPin, label: t('sidebar.locations'), href: "/locations" },
            { icon: Calculator, label: t('sidebar.feeSchedules'), href: "/fee-configs" },
          ]
        }
      ]
    }] : []),
    ...(isAdmin ? [{
      key: "gestione",
      type: "group",
      label: t('sidebar.management'),
      icon: Briefcase,
      items: [
        {
          key: "gestione_pratiche",
          label: "Proposte",
          icon: FileTextIcon,
          items: [
            { icon: FileTextIcon, label: "Preventivi", href: "/quotes" },
          ]
        },
        {
          key: "gestione_anagrafiche",
          label: "Anagrafiche",
          icon: Building2,
          items: [
            { icon: Building2, label: t('sidebar.counterparties'), href: "/controparti" },
            { icon: Contact, label: t('sidebar.contacts'), href: "/contacts" },
          ]
        }
      ]
    }] : []),
    ...(isAdmin ? [{
      key: "hr_candidati",
      type: "group",
      label: "HR Candidati",
      icon: Users,
      items: [
        { icon: GraduationCap, label: "Titoli di studio", href: "/hr/titoli-studio" },
        { icon: FileTextIcon, label: "Fonti", href: "/hr/fonti" },
        { icon: Briefcase, label: "Posizioni", href: "/hr/posizioni" },
        { icon: Users, label: "Candidati", href: "/hr/candidati" },
        { icon: Calendar, label: "Colloqui", href: "/hr/colloqui" },
        { icon: BarChart3, label: "KPI HR", href: "/hr/kpi" }
      ]
    }] : []),
    ...(canAccessHrCore ? [{
      key: "risorse_umane",
      type: "group",
      label: "Risorse Umane",
      icon: UserCheck,
      items: [
        ...(canManageHrAnagrafiche ? [{ icon: LayoutDashboard, label: "Dashboard HR", href: "/hr/risorse-umane" }] : []),
        { icon: Fingerprint, label: isLegalDepartment ? "Pratiche" : "Timbra", href: "/hr/timbra" },
        { icon: Plane, label: "Ferie e permessi", href: "/hr/ferie-permessi" },
        { icon: CalendarCheck2, label: isLegalDepartment ? "Riepilogo" : "Presenze", href: "/hr/presenze" },
        ...(isAdmin ? [{ icon: Calendar, label: "Presenze Mensili", href: "/hr/presenze-mensili" }] : []),
        { icon: WalletCards, label: "Cedolini/Compensi", href: "/hr/cedolini-compensi" },
        ...(canManageHrAnagrafiche
          ? [
              { icon: Tags, label: "Tipi assenza", href: "/hr/tipi-assenza" },
              { icon: Clock3, label: "Profili orario", href: "/hr/profili-orario" },
              { icon: IdCard, label: "Dipendenti/Collaboratori", href: "/hr/dipendenti" }
            ]
          : [])
      ]
    }] : []),
    {
      key: "modulistica",
      type: "group",
      label: "Modulistica",
      icon: FileText,
      items: [
        { icon: FileText, label: "Modelli", href: "/modulistica/modelli" },
        { icon: Tags, label: "Categorie", href: "/modulistica/categorie" },
      ]
    },
    {
      key: "generazione_atti_ai",
      type: "group",
      label: "Generazione Atti AI",
      icon: Sparkles,
      items: [
        { icon: Scale, label: "TEGM · Tassi soglia", href: "/atti-ai/tegm" },
        { icon: LineChart, label: "Indicatori Trimestrali", href: "/atti-ai/indicatori-trimestrali" },
        { icon: ClipboardList, label: "Template", href: "/atti-ai/template" },
        { icon: FolderKanban, label: "Pratiche", href: "/atti-ai/pratiche" },
        { icon: LayoutDashboard, label: "Dashboard generazioni", href: "/atti-ai/dashboard-generazioni" }
      ]
    },
    {
      key: "documenti",
      type: "group",
      label: "Archiviazione",
      icon: Archive,
      items: [
        { icon: FolderKanban, label: "Documentale", href: "/documenti" }
      ]
    },
    ...(isAdmin ? [{
      key: "asset_management",
      type: "group",
      label: "Asset Aziendali",
      icon: Boxes,
      items: [
        { icon: Boxes, label: "Asset", href: "/assets" },
        { icon: Tags, label: "Categorie", href: "/assets/categories" },
        { icon: Building2, label: "Fornitori", href: "/assets/suppliers" },
        { icon: Calendar, label: "Planner", href: "/assets/calendar" }
      ]
    }] : []),
    {
      key: "app_lorenzo",
      type: "group",
      label: "APP (Lorenzo)",
      icon: AppWindow,
      items: [
        {
          key: "app_core",
          label: "Parametri",
          icon: Settings,
          items: [
            { icon: AlertCircle, label: "Urgency", href: "/urgency" },
            { icon: Tags, label: "Category Negotiation", href: "/category-negotiation" },
            ...(isAdmin ? [{ icon: Briefcase, label: "Department", href: "/department" }] : []),
            ...(isAdmin ? [{ icon: Building2, label: "Office", href: "/office" }] : []),
            { icon: Users, label: "Cluster", href: "/cluster" },
          ]
        },
        {
          key: "app_registry",
          label: "Anagrafiche",
          icon: Users,
          items: [
            { icon: Building2, label: "Banche e Finanziarie", href: "/issuer" },
            { icon: Users, label: "Clienti", href: "/customer" },
            { icon: ClipboardList, label: "Dettaglio Cliente", href: "/customer/cumulativa" },
            { icon: Contact, label: "Contatto", href: "/contatto" },
          ]
        },
        {
          key: "app_quotes",
          label: "Preventivi",
          icon: FileTextIcon,
          items: [
            { icon: Headphones, label: "Recall", href: "/dossier/recall" },
          ]
        },
        {
          key: "app_practices",
          label: "Gestione",
          icon: FileTextIcon,
          items: [
            { icon: FileTextIcon, label: "Fascicoli", href: "/dossier" },
            { icon: FileTextIcon, label: "Pratiche (storico)", href: "/negotiation" }
          ]
        },
        {
          key: "app_richieste",
          label: "Richieste",
          icon: FileTextIcon,
          items: [
            { icon: Settings, label: "Parametri", href: "/richieste/parametri" },
            { icon: ClipboardList, label: "Fasi gestione", href: "/richieste/fasi-gestione" },
            { icon: FileTextIcon, label: "Regole Modelli", href: "/richieste/regole-proposta-modello" },
            { icon: FileTextIcon, label: "Richieste Documenti", href: "/chiusure/richieste-documenti" }
          ]
        },
        {
          key: "app_monitoring",
          label: "Monitoraggio",
          icon: BarChart3,
          items: [
            { icon: Activity, label: "Monitoraggio pratiche", href: "/pratiche-monitoraggio" },
            { icon: Activity, label: "Monitoraggio clienti", href: "/clienti-monitoraggio" },
            ...(isAdmin
              ? [
                  { icon: BarChart3, label: "Carichi per Legale", href: "/pratiche-carichi-lavoro" },
                  { icon: BarChart3, label: "Carichi per Contabile", href: "/pratiche-carichi-contabile" }
                ]
              : []),
          ]
        },
        {
          key: "app_reports",
          label: "Report & Analisi AI",
          icon: BarChart3,
          items: [
            { icon: BarChart3, label: "KPI Pratiche", href: "/pratiche-kpi" },
            { icon: FileTextIcon, label: "Report Pratiche", href: "/pratiche-report" },
            { icon: Activity, label: "Analisi AI note", href: "/analisi-note-ai" },
            { icon: Activity, label: "Analisi AI task", href: "/analisi-task-ai" }
          ]
        },
        {
          key: "app_chiusure",
          label: "Chiusure & Firme",
          icon: Archive,
          items: [
            { icon: Settings, label: "parametri_chiusure", href: "/chiusure/parametri_chiusure" },
            { icon: Briefcase, label: "Commerciali (Firme)", href: "/chiusure/commerciali_chiusure" },
            { icon: Scale, label: "Legali (Chiusure)", href: "/chiusure/legali_chiusure" },
            { icon: Calculator, label: "Transazioni (Calcolo)", href: "/chiusure/transazioni_calcolo" }
          ]
        },
        {
          key: "app_activities",
          label: "Attività",
          icon: ListTodo,
          items: [
            ...(isAdmin ? [{
              key: "app_activities_params",
              label: "Parametri",
              icon: Settings,
              items: [
                { icon: PlayCircle, label: "Stato Iniziale", href: "/attivita/stato-iniziale" },
                { icon: Activity, label: "Attività Svolta", href: "/attivita/attivita-svolta" },
                { icon: CheckCircle2, label: "Stato Finale", href: "/attivita/stato-finale" },
              ]
            }] : []),
            { icon: ClipboardList, label: "Registro Attività", href: "/attivita/registro" },
            { icon: Calendar, label: "Sintesi Mensile", href: "/attivita/sintesi" },
            { icon: CheckSquare, label: "Task", href: "/tasks" },
          ]
        },
        ...(isAdmin ? [{
          key: "app_permissions",
          label: "Permessi",
          icon: Shield,
          items: [
            { icon: Users, label: t('sidebar.users'), href: "/lorenzo-users" },
            { icon: Lock, label: "Debito Grant", href: "/debito-grant" },
            { icon: UserCheck, label: "Debito Role", href: "/debito-role" },
          ]
        }] : [])
      ]
    },
    {
      key: "recessi",
      type: "group",
      label: "Recessi",
      icon: ScrollText,
      items: [
        ...(isAdmin ? [{ icon: BarChart3, label: "Dashboard KPI", href: "/recessi/kpi" }] : []),
        { icon: ListTodo, label: "Elenco recessi", href: "/recessi" },
        ...(isAdmin ? [{ icon: Tags, label: "Motivazioni", href: "/recessi/motivazioni" }] : []),
        { icon: FileTextIcon, label: "Documenti", href: "/recessi/documenti" }
      ]
    },
    {
      key: "blacklist",
      type: "group",
      label: "Blacklist",
      icon: Ban,
      items: [
        ...(isAdmin ? [{ icon: BarChart3, label: "Dashboard KPI", href: "/blacklist/kpi" }] : []),
        { icon: ClipboardList, label: "Segnalazioni", href: "/blacklist/segnalazioni" },
        ...(isAdmin ? [{ icon: Boxes, label: "Tipi inadempienza", href: "/blacklist/tipi-inadempienza" }] : []),
        ...(isAdmin ? [{ icon: AlertCircle, label: "Richiami", href: "/blacklist/richiami" }] : [])
      ]
    },
    {
      key: "urgenze_opposizioni",
      type: "group",
      label: "Urgenze/Opposizioni",
      icon: Scale,
      items: [
        {
          key: "urgenze_opposizioni_parametri",
          label: "Parametri",
          icon: Settings,
          items: [
            { icon: Tags, label: "Tipi Azione", href: "/tipi-pratica-azione" },
            { icon: FileTextIcon, label: "Tipi Atto", href: "/tipi-atto" },
            { icon: WalletCards, label: "Tipi Posizione Credito", href: "/tipi-posizione-credito" },
            { icon: FolderKanban, label: "Tipi Allegato", href: "/tipi-allegato-uo" },
            { icon: CheckCircle2, label: "Esiti Udienza", href: "/esiti-udienza" }
          ]
        },
        { icon: Scale, label: "Tribunali", href: "/tribunali" },
        { icon: Briefcase, label: "Legali", href: "/legali" },
        { icon: FileTextIcon, label: "Pratiche U/O", href: "/pratiche-urgenze-opposizioni" },
        { icon: Calendar, label: "Calendario U/O", href: "/pratiche-urgenze-opposizioni/calendario" }
      ]
    },
    {
      key: "ader",
      type: "group",
      label: "AdER",
      icon: Landmark,
      items: [
        ...(isAdmin ? [{
          key: "ader_parametri",
          label: "Parametri",
          icon: Settings,
          items: [
            { icon: Settings, label: "Tipi documento", href: "/ader/tipi-documento" }
          ]
        }] : []),
        { icon: FolderKanban, label: "Lista pratiche", href: "/ader/coda-validazione" },
        { icon: Users, label: "Acquisizione cliente", href: "/ader/documenti/upload" },
        { icon: FileText, label: "Analisi documenti", href: "/ader/coda-validazione" },
        { icon: Scale, label: "Valutazione legale", href: "/ader/report-ai" },
        { icon: CheckSquare, label: "Strategia", href: "/ader/report-ai" },
        { icon: FileTextIcon, label: "Report cliente", href: "/ader/report-ai" },
        { icon: Sparkles, label: "Simulazione Claude", href: "/claude/posizione-debitoria" }
      ]
    },
    {
      key: "sovraindebitamento",
      type: "group",
      label: "Sovraindebitamento",
      icon: RefreshCw,
      items: [
        { icon: ListTodo, label: "Lista pratiche", href: "/sovraindebitamento" },
        { icon: FileTextIcon, label: "PRD", href: "/sovraindebitamento/prd" },
        { icon: Scale, label: "Concordato minore", href: "/sovraindebitamento/concordato-minore" },
        { icon: RefreshCw, label: "Liquidazione controllata", href: "/sovraindebitamento/liquidazione-controllata" },
        { icon: CheckCircle2, label: "Esdebitazione", href: "/sovraindebitamento/esdebitazione" }
      ]
    },
    {
      key: "monitoraggio_sic_cai",
      type: "group",
      label: "Monitoraggio SIC e CAI",
      icon: ShieldCheck,
      items: [
        { icon: FileSearch, label: "Visure CRIF", href: "/monitoraggio-sic-cai/crif" },
        { icon: FileSearch, label: "Visure CTC", href: "/monitoraggio-sic-cai/ctc" },
        { icon: AlertTriangle, label: "Visure CAI", href: "/monitoraggio-sic-cai/cai" },
        { icon: Sparkles, label: "Verifica UPSELL", href: "/monitoraggio-sic-cai/upsell" }
      ]
    },
    ...((isAdmin || isAmministrazione) ? [{
      key: "open_banking",
      type: "group",
      label: "Open Banking",
      icon: Building2,
      items: [
        {
          icon: Building2,
          label: "Business Registry",
          href: "/open-banking/business-registry"
        },
        {
          icon: CreditCard,
          label: "Account Bancari",
          href: "/open-banking/accounts"
        },
        {
          icon: ArrowLeftRight,
          label: "Transazioni",
          href: "/open-banking/transactions"
        },
        {
          icon: ArrowLeftRight,
          label: "Transazioni (da csv)",
          href: "/open-banking/transactions-csv"
        },
        {
          icon: FileTextIcon,
          label: "Contabili Bonifici",
          href: "/open-banking/contabili"
        },
        {
          icon: BarChart3,
          label: "Proiezione Flussi",
          href: "/open-banking/flow-projection"
        }
      ]
    }] : []),
    ...(canAccessPraticheFinanziarie ? [{
      key: "pratiche_finanziarie",
      type: "group",
      label: "Pratiche Finanziarie",
      icon: WalletCards,
      items: [
        {
          key: "pratiche_finanziarie_parametri",
          label: "Parametri",
          icon: Settings,
          items: [
            { icon: Tags, label: "Tipi Operazione", href: "/pratiche-finanziarie/parametri/tipi-operazione" },
            { icon: CheckCircle2, label: "Stati Pratica", href: "/pratiche-finanziarie/parametri/stati-pratica" },
            { icon: FileText, label: "Tipi Documento", href: "/pratiche-finanziarie/parametri/tipi-documento" },
            { icon: Building2, label: "Istituti eroganti", href: "/pratiche-finanziarie/parametri/istituti-eroganti" }
          ]
        },
        { icon: Users, label: "Clienti", href: "/pratiche-finanziarie/clienti" },
        { icon: FileText, label: "Documenti pratiche", href: "/pratiche-finanziarie/documenti" },
        { icon: FileTextIcon, label: "Pratiche", href: "/pratiche-finanziarie/pratiche" },
        { icon: AlertCircle, label: "Alert", href: "/pratiche-finanziarie/alert" }
      ]
    }] : []),
    ...(canAccessScadenzario ? [{
      key: "scadenzario",
      type: "group",
      label: "Scadenzario",
      icon: Calendar,
      items: [
        { icon: CreditCard, label: "Piani Rateazione", href: "/scadenzario/piani" },
        { icon: Calendar, label: "Rate Mensili", href: "/scadenzario/rate" },
        { icon: Mail, label: "Invio Promemoria", href: "/scadenzario/invio-promemoria" },
        {
          key: "scadenzario_kpi",
          label: "KPI",
          icon: BarChart3,
          items: [
            { icon: LineChart, label: "Proiezione Piani", href: "/scadenzario/kpi/proiezione-piani" },
            { icon: BarChart3, label: "Referenti Piani", href: "/scadenzario/kpi/referenti-piani" },
            { icon: BarChart3, label: "Performance Referenti", href: "/scadenzario/kpi/performance-referenti" }
          ]
        }
      ]
    }] : []),
    {
      key: "passpartout_gestionale",
      type: "group",
      label: "Passpartout - Gestionale",
      icon: Briefcase,
      items: [
        { icon: Boxes, label: "Risorse API", href: "/risorseapipasspartout" },
        {
          key: "passp_clientes",
          label: "Anagrafica",
          icon: CloudDownload,
          items: [
            { icon: Building2, label: t('sidebar.companies'), href: "/companies" },
            { icon: FileTextIcon, label: "Conti di bilancio", href: "/passp-conti" },
            { icon: Users, label: "Clienti", href: "/passp-clientes" }
          ]
        },
        {
          key: "passp_prima_nota",
          label: "Prima Nota",
          icon: CloudDownload,
          items: [
            { icon: FileTextIcon, label: "Visualizza/Recupera", href: "/passp-prima-nota" }
          ]
        },
        {
          key: "passp_fatture",
          label: "Fatture",
          icon: CloudDownload,
          items: [
            { icon: FileTextIcon, label: "Visualizza/Recupera", href: "/passp-fatture" },
            { icon: BarChart3, label: "Statistiche", href: "/passp-fatture/stats" }
          ]
        },
        {
          key: "passp_esposizione_contabile",
          label: "Esposizione Contabile",
          icon: CloudDownload,
          items: [
            { icon: CheckCircle2, label: "Visualizza", href: "/passp-esposizione-contabile" },
            { icon: Scale, label: "Situazione Clienti", href: "/passp-situazione-contabile" }
          ]
        },
        {
          key: "passp_controlli",
          label: "Controlli",
          icon: CloudDownload,
          items: [
            { icon: CheckSquare, label: "Fatture Vs Prima Nota", href: "/passp-controlli/fatture-vs-prima-nota" }
          ]
        }
      ]
    }
  ]

  const navGroups = isAgency
    ? allNavGroups.filter((g) => g.key === "dashboard" || g.key === "pratiche_finanziarie")
    : allNavGroups

  const rootGroupKeys = navGroups
    .filter(g => g.type === "group")
    .map(g => g.key);

  const isActive = (path) => {
    if (!path) return false;
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname === path;
  }

  // Recursive helper to check if any child is active
  const hasActiveChild = (items) => {
    return items.some(item => {
      if (item.items) return hasActiveChild(item.items);
      return isActive(item.href);
    });
  };

  // Auto-expand group if current path is inside it
  useEffect(() => {
    const newExpanded = { ...expandedGroups };
    let changed = false;

    const traverseAndExpand = (items) => {
      items.forEach(item => {
        if (item.items) {
          if (hasActiveChild(item.items)) {
            if (!newExpanded[item.key]) {
              newExpanded[item.key] = true;
              changed = true;
            }
            traverseAndExpand(item.items);
          }
        }
      });
    };

    navGroups.forEach(group => {
      if (group.type === "group") {
        if (hasActiveChild(group.items)) {
           if (!newExpanded[group.key]) {
             newExpanded[group.key] = true;
             changed = true;
           }
           traverseAndExpand(group.items);
        }
      }
    });

    if (changed) {
      setExpandedGroups(newExpanded);
    }
  }, [location.pathname]);

  const renderSubItems = (items) => {
    // Ottieni tutte le chiavi dei gruppi fratelli a questo livello
    const siblingKeys = items
      .filter(item => item.items)
      .map(item => item.key);

    return items.map((item, idx) => {
      if (item.items) {
        // Sub-menu
        const isExpanded = expandedGroups[item.key];
        const activeChild = hasActiveChild(item.items);
        
        return (
          <div key={item.key || idx} className="space-y-1 pt-1">
             <Button
                variant="ghost"
                size="sm"
                className={cn(
                  "w-full justify-between gap-3 h-9",
                  activeChild && !isExpanded && "text-primary"
                )}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleGroup(item.key, siblingKeys);
                }}
             >
                <div className="flex items-center gap-3">
                  {item.icon && <item.icon className="h-4 w-4" />}
                  <span className="font-medium">{item.label}</span>
                </div>
                {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
             </Button>
             
             {isExpanded && (
               <div className="pl-3 space-y-1 border-l ml-4 border-gray-100">
                 {renderSubItems(item.items)}
               </div>
             )}
          </div>
        );
      } else {
        // Link item
        if (item.href === "/ader/documenti/upload") {
          return (
            <div key={idx}>
              <Button
                variant={isActive(item.href) ? "secondary" : "ghost"}
                size="sm"
                className={cn(
                  "w-full justify-start gap-3 h-9",
                  isActive(item.href) && "bg-primary/10 text-primary hover:bg-primary/20 font-medium"
                )}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setAderUploadInfoOpen(true)
                }}
              >
                {item.icon && <item.icon className="h-4 w-4 opacity-70" />}
                <span>{item.label}</span>
              </Button>
            </div>
          )
        }
        if (item.href === "/ader/coda-validazione") {
          return (
            <div key={idx}>
              <Button
                variant={isActive(item.href) ? "secondary" : "ghost"}
                size="sm"
                className={cn(
                  "w-full justify-start gap-3 h-9",
                  isActive(item.href) && "bg-primary/10 text-primary hover:bg-primary/20 font-medium"
                )}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setAderCodaValidazioneInfoOpen(true)
                }}
              >
                {item.icon && <item.icon className="h-4 w-4 opacity-70" />}
                <span>{item.label}</span>
              </Button>
            </div>
          )
        }
        return (
          <Link key={idx} to={item.href}>
            <Button
              variant={isActive(item.href) ? "secondary" : "ghost"}
              size="sm"
              className={cn(
                "w-full justify-start gap-3 h-9",
                isActive(item.href) && "bg-primary/10 text-primary hover:bg-primary/20 font-medium"
              )}
            >
              {item.icon && <item.icon className="h-4 w-4 opacity-70" />}
              <span>{item.label}</span>
            </Button>
          </Link>
        );
      }
    });
  };

  return (
    <aside 
      className={cn(
        "flex flex-col border-r border-transparent lg:border-gray-100 bg-card transition-all duration-300 ease-in-out h-[100dvh] sticky top-0 z-30",
        collapsed ? "w-20" : "w-64"
      )}
    >
      <div className={cn(
        "flex items-center justify-between px-4 border-b border-gray-100 relative transition-all duration-300",
        collapsed ? "h-24 flex-col justify-between py-2" : "h-32"
      )}>
        {!collapsed && (
          <div className="flex items-center gap-2 w-full justify-center">
            <img src="/logo.svg" alt="Logo" className="h-20 w-auto object-contain dark:invert" />
          </div>
        )}
        
        {collapsed && (
           <div className="flex flex-col items-center gap-2">
             <Button 
                variant="ghost" 
                size="icon" 
                className="h-7 w-7" 
                onClick={() => setCollapsed(!collapsed)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
             <div className="h-10 w-10 rounded-xl border border-gray-200 bg-white p-1.5 shadow-sm dark:border-slate-700 dark:bg-slate-900">
               <img src="/logo.svg" alt="Logo" className="h-full w-full object-cover object-top dark:invert" />
             </div>
           </div>
        )}
        
        {!collapsed && (
          <Button 
            variant="ghost" 
            size="icon" 
            className="hidden md:flex absolute right-2 top-2" 
            onClick={() => setCollapsed(!collapsed)}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="flex-1 py-4 overflow-y-auto">
        <nav className="grid gap-1 px-2">
          {navGroups.map((group) => {
            if (group.type === "single") {
              const item = group.item;
              return (
                <Link
                  key={group.key}
                  to={item.href}
                >
                  <Button
                    variant={isActive(item.href) ? "secondary" : "ghost"}
                    className={cn(
                      "w-full justify-start gap-3", 
                      collapsed && "justify-center px-2",
                      isActive(item.href) && "bg-primary/10 text-primary hover:bg-primary/20"
                    )}
                    title={collapsed ? item.label : undefined}
                  >
                    <item.icon className="h-5 w-5" />
                    {!collapsed && <span>{item.label}</span>}
                  </Button>
                </Link>
              );
            } else {
              // Group
              const isExpanded = expandedGroups[group.key];
              const hasActiveItem = group.items.some(item => isActive(item.href));
              
              // When collapsed, we can't show accordion easily.
              // Strategy: 
              // 1. Show group header icon.
              // 2. If clicked, expand sidebar.
              // 3. Render children only if expanded (visually).
              
              return (
                <div key={group.key} className="space-y-1">
                  <Button
                    variant="ghost"
                    className={cn(
                      "w-full justify-between gap-3 font-medium",
                      collapsed && "justify-center px-2",
                      hasActiveItem && !isExpanded && "text-primary"
                    )}
                    onClick={() => toggleGroup(group.key, rootGroupKeys)}
                    title={collapsed ? group.label : undefined}
                  >
                    <div className="flex items-center gap-3">
                      <group.icon className="h-5 w-5" />
                      {!collapsed && <span>{group.label}</span>}
                    </div>
                    {!collapsed && (
                      isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />
                    )}
                  </Button>

                  {/* Children - Only show if sidebar is NOT collapsed AND group is expanded */}
                  {!collapsed && isExpanded && (
                    <div className="pl-4 space-y-1 border-l ml-4 border-gray-100">
                      {renderSubItems(group.items)}
                    </div>
                  )}

                  {/* If collapsed, maybe we just show the active item icon if exists? 
                      Or we force user to expand. 
                      Let's just show nothing extra when collapsed, user clicks header to expand sidebar.
                  */}
                </div>
              );
            }
          })}
        </nav>
      </div>

      <Dialog open={aderUploadInfoOpen} onClose={() => setAderUploadInfoOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Carica documenti ADER</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Questa sezione consente di caricare uno o più PDF ADER. Dopo l’upload il sistema esegue una estrazione base
            e poi, in base al tipo documento, applica estrattori specifici.
          </Typography>

          <Box sx={{ display: "grid", gap: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: 1.5,
                  bgcolor: "#e0f2fe",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: "0 0 auto"
                }}
              >
                <CloudDownload className="h-5 w-5" color="#0284c7" />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Salvataggio su Wasabi
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Il file viene archiviato su storage e referenziato nel database.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: 1.5,
                  bgcolor: "#dcfce7",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: "0 0 auto"
                }}
              >
                <FileTextIcon className="h-5 w-5" color="#16a34a" />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Estrazione testo
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Estrae testo dai PDF nativi. Se il testo è insufficiente (o il PDF è una scansione), il documento va in
                  coda per verifica.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: 1.5,
                  bgcolor: "#ede9fe",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: "0 0 auto"
                }}
              >
                <Sparkles className="h-5 w-5" color="#7c3aed" />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  AI & matching cliente
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Classifica il documento, estrae i dati principali e propone un cliente.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: 1.5,
                  bgcolor: "#fff7ed",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: "0 0 auto"
                }}
              >
                <ClipboardList className="h-5 w-5" color="#c2410c" />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Estrazione per tipo
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Esempi: “estratto di ruolo” → righe tabellari; “cartella di pagamento” → voci/tributi e dettaglio
                  importi.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: 1.5,
                  bgcolor: "#fef3c7",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: "0 0 auto"
                }}
              >
                <AlertCircle className="h-5 w-5" color="#d97706" />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Validazione operatore
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  I risultati finiscono in coda per conferma o correzione.
                </Typography>
              </Box>
            </Box>
          </Box>

          <Typography variant="body2" sx={{ mt: 2 }}>
            I documenti finiscono poi nella coda di validazione per conferma operatore (es. confidence bassa, testo
            insufficiente, matching “in attesa” o errori).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="ghost" onClick={() => setAderUploadInfoOpen(false)}>
            Annulla
          </Button>
          <Button
            onClick={() => {
              setAderUploadInfoOpen(false)
              navigate("/ader/documenti/upload")
            }}
          >
            Vai al caricamento
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={aderCodaValidazioneInfoOpen}
        onClose={() => setAderCodaValidazioneInfoOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Coda validazione ADER</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Qui trovi i documenti ADER da monitorare e validare. La coda include anche documenti appena caricati o in
            elaborazione.
          </Typography>

          <Typography variant="body2" sx={{ mb: 2 }}>
            Un documento finisce qui quando serve intervento operatore (es. confidence sotto 90, testo insufficiente,
            matching “in attesa” o errore di elaborazione).
          </Typography>

          <Box sx={{ display: "grid", gap: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: 1.5,
                  bgcolor: "#e0f2fe",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: "0 0 auto"
                }}
              >
                <ClipboardList className="h-5 w-5" color="#0284c7" />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Stato ed esito
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Visualizza lo stato (caricato, in elaborazione, da validare, errore) e l’esito dell’estrazione.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: 1.5,
                  bgcolor: "#dcfce7",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: "0 0 auto"
                }}
              >
                <UserCheck className="h-5 w-5" color="#16a34a" />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Validazione matching
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Conferma o correggi il cliente proposto prima di finalizzare l’elaborazione.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: 1.5,
                  bgcolor: "#ede9fe",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: "0 0 auto"
                }}
              >
                <RefreshCw className="h-5 w-5" color="#7c3aed" />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Rielaborazione
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Se serve, rilancia l’elaborazione del documento (estrazione/AI/matching).
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: 1.5,
                  bgcolor: "#fef3c7",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: "0 0 auto"
                }}
              >
                <AlertCircle className="h-5 w-5" color="#d97706" />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Gestione errori
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Individua rapidamente documenti in errore e apri il dettaglio per capire cosa è successo.
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="ghost" onClick={() => setAderCodaValidazioneInfoOpen(false)}>
            Annulla
          </Button>
          <Button
            onClick={() => {
              setAderCodaValidazioneInfoOpen(false)
              navigate("/ader/coda-validazione")
            }}
          >
            Vai alla coda
          </Button>
        </DialogActions>
      </Dialog>
    </aside>
  )
}
